// Helper: extract YouTube video ID from URL
export function extractYouTubeId(url: string): string | null {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

// Helper: get static thumbnail URL from SNS URL (no API call needed)
export function getStaticThumbnail(url: string): string | null {
  if (!url) return null;
  const ytId = extractYouTubeId(url);
  if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  return null;
}

// Helper: fetch oEmbed thumbnail for platforms that support it
export async function fetchOEmbedThumbnail(
  url: string
): Promise<string | null> {
  if (!url) return null;

  const endpoints: { pattern: RegExp; endpoint: string }[] = [
    {
      pattern: /spotify\.com|open\.spotify/i,
      endpoint: `https://open.spotify.com/oembed?url=${encodeURIComponent(
        url
      )}`
    },
    {
      pattern: /soundcloud\.com/i,
      endpoint: `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(
        url
      )}`
    },
    {
      pattern: /tiktok\.com/i,
      endpoint: `https://www.tiktok.com/oembed?url=${encodeURIComponent(
        url
      )}`
    }
  ];

  for (const ep of endpoints) {
    if (ep.pattern.test(url)) {
      try {
        const res = await fetch(ep.endpoint);
        if (res.ok) {
          const data = (await res.json()) as { thumbnail_url?: string };
          return data.thumbnail_url ?? null;
        }
      } catch (e) {
        // Log the error for debugging but continue gracefully
        console.error('Failed to fetch oEmbed thumbnail:', e);
      }
      return null;
    }
  }
  return null;
}

// Helper: detect SNS platform from URL
export function detectPlatformFromUrl(
  url: string
):
  | { name: string; icon: string; color: string }
  | null {
  if (!url || !url.trim()) return null;

  const patterns: {
    pattern: RegExp;
    name: string;
    icon: string;
    color: string;
  }[] = [
    {
      pattern: /youtube\.com|youtu\.be/i,
      name: 'YouTube',
      icon: 'ri-youtube-line',
      color: '#FF0000'
    },
    {
      pattern: /instagram\.com/i,
      name: 'Instagram',
      icon: 'ri-instagram-line',
      color: '#E4405F'
    },
    {
      pattern: /twitter\.com|x\.com/i,
      name: 'Twitter / X',
      icon: 'ri-twitter-x-line',
      color: '#ffffff'
    },
    {
      pattern: /soundcloud\.com/i,
      name: 'SoundCloud',
      icon: 'ri-soundcloud-line',
      color: '#FF5500'
    },
    {
      pattern: /spotify\.com|open\.spotify/i,
      name: 'Spotify',
      icon: 'ri-spotify-line',
      color: '#1DB954'
    },
    {
      pattern: /tiktok\.com/i,
      name: 'TikTok',
      icon: 'ri-tiktok-line',
      color: '#ffffff'
    },
    {
      pattern: /music\.apple\.com/i,
      name: 'Apple Music',
      icon: 'ri-apple-line',
      color: '#FC3C44'
    },
    {
      pattern: /bandcamp\.com/i,
      name: 'Bandcamp',
      icon: 'ri-music-2-line',
      color: '#1DA0C3'
    },
    {
      pattern: /facebook\.com|fb\.com/i,
      name: 'Facebook',
      icon: 'ri-facebook-line',
      color: '#1877F2'
    },
    {
      pattern: /line\.me/i,
      name: 'LINE',
      icon: 'ri-line-line',
      color: '#06C755'
    },
    {
      pattern: /nicovideo\.jp|nico\.ms/i,
      name: 'niconico',
      icon: 'ri-play-circle-line',
      color: '#ffffff'
    },
    {
      pattern: /twitch\.tv/i,
      name: 'Twitch',
      icon: 'ri-twitch-line',
      color: '#9146FF'
    },
    {
      pattern: /threads\.net/i,
      name: 'Threads',
      icon: 'ri-at-line',
      color: '#ffffff'
    },
    {
      pattern: /linktr\.ee/i,
      name: 'Linktree',
      icon: 'ri-leaf-line',
      color: '#43E55E'
    }
  ];

  for (const p of patterns) {
    if (p.pattern.test(url)) {
      return { name: p.name, icon: p.icon, color: p.color };
    }
  }

  // Fallback for generic websites
  if (/^https?:\/\//i.test(url) || url.includes('.')) {
    return { name: 'Website', icon: 'ri-global-line', color: '#00d9ff' };
  }

  return null;
}
